/**
 * TutorialController — the guided onboarding flow. Owns a fully ISOLATED 3D
 * scene (its own GameScene + DiceController + CameraController) that never
 * touches the real game bootstrap or a game-server socket. It drives the
 * CoachMarkOverlay step-by-step off the live dice/betting events and reports
 * each cleared module to the server (which grants the reward idempotently).
 *
 * E1 scope: Module 1 — "Learn to Shoot" (craps, 2 dice, land 3 throws against
 * the back wall). Wall hits are a deterministic, bounded goal (always 3 throw
 * reps) — unlike "roll a 7", which could clear instantly or drag on for ages.
 * Module 2 ("Learn the Wallet") is wired in a later pass; the hook is marked.
 *
 * Lifecycle: start() shows the scene + runs the engine loop; exit()/skip()
 * tears the scene down and restores the home UI via the onExit callback.
 */

import * as THREE from 'three';
import { CrapsState, type GameUpdate } from '@sdl/shared';
import { Engine } from '../../engine/Engine';
import { DiceController } from '../dice/DiceController';
import { CameraController } from '../camera/CameraController';
import { GameScene } from '../scene/GameScene';
import { DiceAssetManager } from '../dice/assets/DiceAssetManager';
import { GameFlowController } from '../GameFlowController';
import { BettingController, BettingHUD3D } from '../betting';
import { getDenominations } from '../betting/BillDenominations';
import { fetchCurrencyNotes } from '../../api/currencyNoteApi';
import { AssetLoader } from '../../engine/loaders/AssetLoader';
import { NetworkManager } from '../../network/NetworkManager';
import { PlayerProfilesOverlay } from '../../ui/components/PlayerProfilesOverlay';
import { CoachMarkOverlay, ScreenRect } from '../../ui/components/CoachMarkOverlay';
import { TutorialRewardModal } from '../../ui/components/TutorialRewardModal';
import type * as CANNON from 'cannon-es';

/** Minimal shape of the cannon-es 'collide' event we read for wall hits. */
type WallCollideEvent = {
  body?: { material?: { name?: string } | null } | null;
  contact: { getImpactVelocityAlongNormal(): number };
};

export interface TutorialDeps {
  engine: Engine;
  networkManager: NetworkManager;
  /** The shared game-flow controller — Module 2 reuses it (wires the betting
   *  subsystems in, pushes scripted updates, resets it on teardown) exactly
   *  like setupGameScene / cleanupGameScene do for a real game. */
  gameFlowController: GameFlowController;
  /** Restored on the gfc after Module 2 (hidden during the tutorial). */
  playerProfilesOverlay: PlayerProfilesOverlay | null;
  diceAssetManager: DiceAssetManager | null;
  cdnBase: string | null;
  /** The player's equipped craps dice (their red default), so the tutorial
   *  dice match the loadout the account spawned with. */
  equippedDice: string[];
  /** Dice codes granted by each module (for the reward reveal thumbnails). */
  rewardDice: { shoot: string; bet: string };
  /** Restore the home UI (caller hides the game container + shows UIManager). */
  onExit: () => void;
}

export class TutorialController {
  private deps: TutorialDeps;
  private engine: Engine;

  private scene: GameScene | null = null;
  private dice: DiceController | null = null;
  private camera: CameraController | null = null;
  private coach: CoachMarkOverlay;
  private reward: TutorialRewardModal;

  // Module 2 (wallet) betting subsystems.
  private betting: BettingController | null = null;
  private bettingHUD: BettingHUD3D | null = null;
  private bettingAssetLoader: AssetLoader | null = null;
  private unsubBetting: (() => void) | null = null;
  private module2Active = false;
  private module2Step = 0;
  private stepBusy = false;
  // Version for the scripted GameUpdates we push through the shared
  // GameFlowController. Starts low and only needs to be monotonic within the
  // tutorial — must NOT be Date.now(), which would poison the controller's
  // game:update dedup high-water mark and make the next real game's small
  // stateVersions look stale (GameFlowController.resetStateMachine clears it too).
  private updateVersion = 0;

  private unsubUpdate: (() => void) | null = null;
  private winTimer: number | null = null;
  private active = false;

  // Module 1 goal: land WALL_HIT_GOAL throws that bounce off the back wall.
  // A "hit" is a wall contact at >= MIN_IMPACT (same threshold the audio uses
  // for an audible wall smack), so a soft settle against the wall doesn't count.
  // Counted once per throw (on dice:stopped), not per contact point.
  private static readonly WALL_HIT_GOAL = 3;
  private static readonly WALL_HIT_MIN_IMPACT = 1.5; // m/s along the contact normal
  private wallHits = 0;
  private wallHitThisThrow = false;
  private wallListeners: Array<{ body: CANNON.Body; handler: (e: WallCollideEvent) => void }> = [];

  // Reusable scratch vector for world→screen projection.
  private readonly _v = new THREE.Vector3();

  constructor(deps: TutorialDeps) {
    this.deps = deps;
    this.engine = deps.engine;
    // highlight:false → instruction banner only (no dim/ring/arrow) for now.
    this.coach = new CoachMarkOverlay({ showSkip: true, highlight: false });
    this.reward = new TutorialRewardModal();
    this.coach.on('skip', () => this.handleSkip());
  }

  /** Build the isolated scene, start the render loop, and begin Module 1. */
  async start(): Promise<void> {
    if (this.active) return;
    this.active = true;
    await this.buildScene();
    this.engine.start();
    this.coach.show();
    this.runModule1();
  }

  // ---- scene -------------------------------------------------------------

  private async buildScene(): Promise<void> {
    this.scene = new GameScene(this.engine, {
      tableWidth: 12,
      tableDepth: 16,
      wallHeight: 8,
      gameModeId: 'craps',
    });
    await this.scene.initialize();

    this.dice = new DiceController(this.engine, {
      diceCount: 2,
      energyGain: 0.3,
      maxEnergy: 100,
      minEnergyToThrow: 5,
      maxPower: 35,
      baseForward: 5,
      holdHeight: 4,
      pickupRadius: 1.5,
    });
    // Skin the tutorial dice with the player's equipped red craps dice so they
    // match the loadout the account spawned with (preload before createDice,
    // with a timeout so a slow CDN can't stall the tutorial).
    const equipped = this.deps.equippedDice.filter(Boolean);
    if (this.deps.diceAssetManager) {
      this.dice.setAssetManager(this.deps.diceAssetManager);
      if (equipped.length > 0) {
        this.dice.setEquippedDice(equipped);
        await Promise.race([
          this.deps.diceAssetManager.preload(equipped),
          new Promise((resolve) => setTimeout(resolve, 5000)),
        ]);
      }
    }

    this.camera = new CameraController(this.engine.camera, {
      defaultPosition: { x: 0, y: 14, z: 10 },
      defaultLookAt: { x: 0, y: 0, z: 0 },
      lerpSpeed: 3,
      zoomLerpSpeed: 2,
      minHeight: 6,
      padding: 2.5,
      diceSize: 1,
    });

    // Per-frame tick for just the tutorial subsystems (the real game loop is
    // not registered on this path). Unsubscribed in teardown().
    this.unsubUpdate = this.engine.onUpdate((dt) => {
      this.dice?.update(dt);
      this.camera?.update(dt);
      this.scene?.update(dt);
    });

    // Camera choreography mirrors the in-game dice flow.
    this.dice.on('dice:pickup', () => this.camera?.setMode('zoom_unheld'));
    this.dice.on('dice:all_collected', () => this.onAllCollected());
    this.dice.on('dice:rolling', () => {
      this.camera?.setMode('follow');
      // New throw in flight — clear the per-throw wall-hit flag so contacts from
      // here until the dice stop decide whether THIS throw counts.
      this.wallHitThisThrow = false;
    });
    this.dice.on('dice:stopped', () => this.onDiceStopped());
  }

  // ---- Module 1: Learn to Shoot -----------------------------------------

  private runModule1(): void {
    if (!this.dice || !this.camera) return;
    this.dice.createDice([
      { x: -1, y: 1, z: 4 },
      { x: 1, y: 1, z: 4 },
    ]);
    // Hand the dice to the camera so it frames ALL of them (zoom-to-fit), the
    // same behaviour as live craps/cee-lo.
    this.camera.setDice(this.dice.getDice());
    this.dice.setEnabled(true);
    this.camera.setMode('default');
    // Count wall hits on each die's physics body (isolated to the tutorial's own
    // dice — no shared dice/game code is touched).
    this.attachWallListeners();
    this.coach.setStep({
      text: 'Tap and hold the dice to pick them up.',
      subtext: 'Then flick forward and release to throw.',
      target: () => this.diceClusterRect(),
      bannerPosition: 'bottom',
    });
  }

  /** Add a wall-hit listener to each tutorial die's body. */
  private attachWallListeners(): void {
    for (const die of this.dice?.getDice() ?? []) {
      const body = die.body;
      const handler = (e: WallCollideEvent): void => {
        if (!this.active || die.held) return;
        if (e.body?.material?.name !== 'wall') return;
        if (Math.abs(e.contact.getImpactVelocityAlongNormal())
            < TutorialController.WALL_HIT_MIN_IMPACT) return;
        // Mark the throw as a wall hit; it's tallied once on dice:stopped.
        this.wallHitThisThrow = true;
      };
      body.addEventListener('collide', handler as (e: unknown) => void);
      this.wallListeners.push({ body, handler });
    }
  }

  /** Remove the wall-hit listeners (bodies are pooled — never leak onto reuse). */
  private detachWallListeners(): void {
    for (const { body, handler } of this.wallListeners) {
      body.removeEventListener('collide', handler as (e: unknown) => void);
    }
    this.wallListeners = [];
  }

  private onAllCollected(): void {
    if (!this.active) return;
    this.camera?.setMode('default');
    this.coach.setStep({
      text: 'Flick forward and release to throw.',
      subtext: this.wallGoalSubtext(),
      bannerPosition: 'bottom',
    });
  }

  /** Subtext for the throw step, reflecting wall-hit progress. */
  private wallGoalSubtext(): string {
    const goal = TutorialController.WALL_HIT_GOAL;
    if (this.wallHits === 0) return `Bounce the dice off the back wall ${goal} times to pass.`;
    return `Back wall ${this.wallHits} of ${goal}. Keep going.`;
  }

  private onDiceStopped(): void {
    if (!this.active) return;
    this.camera?.setMode('zoom');

    const goal = TutorialController.WALL_HIT_GOAL;
    if (this.wallHitThisThrow) {
      this.wallHits += 1;
      if (this.wallHits >= goal) {
        this.completeModule1();
        return;
      }
      this.coach.setStep({
        text: `Nice hit! ${this.wallHits} of ${goal}.`,
        subtext: 'Pick the dice back up and throw again.',
        target: () => this.diceClusterRect(),
        bannerPosition: 'bottom',
      });
    } else {
      this.coach.setStep({
        text: 'You missed the wall.',
        subtext: 'Throw harder so the dice bounce off the back wall.',
        target: () => this.diceClusterRect(),
        bannerPosition: 'bottom',
      });
    }
  }

  private completeModule1(): void {
    // Server grants the red die idempotently.
    this.deps.networkManager.completeTutorialModule('shoot');
    this.dice?.setEnabled(false);
    this.coach.hide();

    // Short beat so the final throw lands visibly against the wall before the
    // reward modal, then: congrats → dice-unlock reveal → Module 2. (The old
    // dice-face flash is gone — the goal is wall hits now, not the rolled value.)
    this.winTimer = window.setTimeout(() => {
      if (!this.active) return;
      this.reward.show({
        heading: 'CONGRATS!',
        message: 'You worked the back wall like a real shooter. You’ve got the hang of it.',
        buttonLabel: 'UNLOCK REWARD',
      });
      this.reward.once('continue', () => {
        if (!this.active) return;
        this.reward.show({
          heading: 'NEW DICE UNLOCKED',
          message: 'A fresh pair for your collection.',
          dieThumbnailUrl: this.thumb(this.deps.rewardDice.shoot),
          dieLabel: this.deps.rewardDice.shoot,
          buttonLabel: 'CONTINUE',
        });
        // Continue → Module 2 (Learn the Wallet).
        this.reward.once('continue', () => { void this.startModule2(); });
      });
    }, 700);
  }

  // ---- Module 2: Learn the Wallet (E2a foundation) ----------------------

  /**
   * E2a — wire the real betting stack into the shared GameFlowController and
   * fire a scripted SIDEBET_BETTING update so the v2 state machine runs the
   * genuine side-betting transition (camera lerp + light crossfade + wallet
   * slide-in + bet zones). Then lock the local drop zone and show step 1.
   * The per-step state machine (cap / cancel / swap / unlock / place / fade)
   * lands in E2b.
   */
  private async startModule2(): Promise<void> {
    if (!this.active || !this.scene || !this.dice || !this.camera) return;
    this.module2Active = true;
    const engine = this.engine;
    const gfc = this.deps.gameFlowController;

    // Load the server currency notes so the wallet's denomination wheel matches
    // real games (otherwise it falls back to the hardcoded 20-entry list).
    await fetchCurrencyNotes().catch(() => { /* fall back to hardcoded */ });
    if (!this.active) return;

    // Build the betting subsystems (mirrors setupGameScene's betting half).
    const q = engine.renderer.getBettingQualitySettings();
    this.bettingAssetLoader = new AssetLoader({
      quality: q.walletModel as 'low' | 'medium' | 'high',
      basePath: '/assets',
    });
    this.betting = new BettingController(engine, this.bettingAssetLoader, {
      initialBalance: 10000,
      minBet: 100,
      maxBet: 1000000,
      swipeThreshold: 30,
      uiContainer: document.getElementById('ui-container') ?? undefined,
      billSegments: q.billSegments,
      billShadows: q.billShadows,
      billTextureSize: q.billTextureSize,
    });
    await this.betting.initialize();
    if (!this.active) return; // bailed during async init

    this.bettingHUD = new BettingHUD3D({
      scene: engine.scene,
      camera: engine.camera,
      assetLoader: this.bettingAssetLoader ?? undefined,
    });
    this.bettingHUD.attachInput(engine.canvas);
    // Suppress the lock-in button for the tutorial (its own disposable HUD
    // instance — real games use a separate one, so this is fully isolated).
    this.bettingHUD.disableLockIn();

    // Step 1 pulls up 1K notes — start the wallet on the 1K denomination.
    const i1k = getDenominations().indexOf(1000);
    if (i1k >= 0) this.betting.getWallet().setDenomination(i1k, { animate: false });

    // Suppress the wallet's own gesture hints ("TAP TO PULL A BILL" / "HOLD TO
    // SWAP"). The coach already teaches each gesture step-by-step in Module 2,
    // so the auto-popping labels would stack on the banner and confuse.
    this.betting.getWallet().getInstructionsLabel()?.disable();
    // Same for the accumulator-stack hints ("DRAG OVER SLOT TO PLACE BET" /
    // "TAP ON BILL TO REMOVE") — they auto-show the moment the stack has
    // bills, right when the coach banner is teaching the same gesture.
    this.betting.getAccumulatorLabel()?.disable();

    // Dropping the 10K bet (step 3) completes Module 2.
    const onBetPlaced = () => {
      if (this.module2Step === 3 && !this.stepBusy) void this.completeModule2();
    };
    this.betting.on('betting:bill_placed', onBetPlaced);
    this.betting.on('betting:sidebet_type_requested', onBetPlaced);

    // Wire into the shared controller — same setters as setupGameScene. These
    // setters call primeBettingScene() using the craps scene's betting anchor.
    gfc.setEngine(engine);
    gfc.setDiceController(this.dice);
    gfc.setCameraController(this.camera);
    gfc.setCameraAnchors(this.scene.getCameraAnchors());
    gfc.setAnchoredLights(this.scene.getAnchoredLights());
    gfc.setBettingController(this.betting);
    gfc.setBettingHUD3D(this.bettingHUD);
    gfc.resetStateMachine();

    // Tick the betting subsystems + controller + the step machine each frame.
    this.unsubBetting = engine.onUpdate((dt) => {
      this.betting?.update(dt);
      this.bettingHUD?.update(dt);
      gfc.update(dt);
      this.tickModule2();
    });

    // Hide the player profile cards for the tutorial (the reused overlay would
    // render a "You"/"Shooter" card). Restored on teardown.
    gfc.setPlayerProfilesOverlay(null);

    // Fire the scripted side-betting update → SidebetBettingState.onEnter →
    // enterBettingMode (camera + lights + wallet + zones). previousState is a
    // NON-betting craps state (POINT_ROLL) so enteringFromNonBetting=true and
    // the light crossfade + wallet hide-then-slide both run.
    this.pushUpdate(CrapsState.SIDEBET_BETTING, CrapsState.POINT_ROLL);

    // Lock the local drop zone (lock icon) and start the step machine at step 0.
    this.betting.setCreateZoneLocked(true);
    this.coach.show();
    this.module2Step = 0;
    this.applyModule2Step();
  }

  /** Per-frame completion check for the active wallet step. */
  private tickModule2(): void {
    if (!this.betting || this.stepBusy || !this.module2Active) return;
    switch (this.module2Step) {
      case 0: // PullUp5K — done when the accumulator hits 5K (input then froze).
        if (this.betting.getPendingBetAmount() >= 5000) void this.advanceModule2();
        break;
      case 1: // Cancel5K — done when the accumulator is cleared.
        if (this.betting.getPendingBetAmount() === 0) void this.advanceModule2();
        break;
      case 2: // Swap10K — done when the wallet is on the 10K note.
        if (this.betting.getCurrentDenomination() === 10000) void this.advanceModule2();
        break;
      // case 3 (Place10K) — placement/fade completion lands in the next pass.
    }
  }

  /** onExit of the current step → onEnter of the next, with the success beat
   *  between (icon + dark-blue bg + label out → label in). Input is frozen for
   *  the beat so a stray tap can't overshoot (this also enforces the 5K cap). */
  private async advanceModule2(): Promise<void> {
    this.stepBusy = true;
    this.betting?.setInputEnabled(false);
    await this.coach.playStepComplete();
    if (!this.active) return;
    this.module2Step += 1;
    this.applyModule2Step();
    this.betting?.setInputEnabled(true);
    this.stepBusy = false;
  }

  /** onEnter for each wallet step: its instruction (centered) + constraints. */
  private applyModule2Step(): void {
    // Denomination swapping is only allowed during the swap step.
    this.betting?.setSwapEnabled(this.module2Step === 2);
    switch (this.module2Step) {
      case 0:
        this.coach.setStep({
          text: 'Tap the wallet to pull up 5K.',
          subtext: 'Each tap stacks a 1K note.',
          bannerPosition: 'center',
        });
        break;
      case 1:
        this.coach.setStep({
          text: 'Cancel it. Tap the notes to clear them.',
          subtext: 'Tap the stacked bills to remove each.',
          bannerPosition: 'center',
        });
        break;
      case 2:
        this.coach.setStep({
          text: 'Tap and hold the wallet, swap to the 10K note.',
          subtext: 'Hold, then pick 10K from the wheel.',
          bannerPosition: 'center',
        });
        break;
      case 3:
        // The 10K is selected → unlock the local drop zone for the bet.
        this.betting?.setCreateZoneLocked(false);
        this.coach.setStep({
          text: 'Now drop a 10K bet on your zone.',
          subtext: 'Pull up a 10K note and drag it in.',
          bannerPosition: 'center',
        });
        break;
    }
  }

  /**
   * Module 2 complete — the 10K bet landed. Play the success beat, grant the
   * black die + 100M (server), reveal it like the shooting tutorial, then exit
   * to the main screen.
   */
  private async completeModule2(): Promise<void> {
    if (this.stepBusy) return;
    this.stepBusy = true;
    this.betting?.setInputEnabled(false);

    this.deps.networkManager.completeTutorialModule('bet'); // black die (no money)

    await this.coach.playStepComplete(); // "✓" + dark-blue + label out
    if (!this.active) return;
    this.coach.hide();

    // Beat 1: congrats.
    this.reward.show({
      heading: 'NICE BET!',
      message: 'You worked the wallet like a pro.',
      buttonLabel: 'NEXT',
    });
    this.reward.once('continue', () => {
      if (!this.active) return;
      // Beat 2: dice unlock (flip + confetti) → exit. Money is NOT a tutorial
      // reward — the 100M is the connect-account bonus, surfaced after exit.
      this.reward.show({
        heading: 'NEW DICE UNLOCKED',
        message: 'A fresh pair for your collection.',
        dieThumbnailUrl: this.thumb(this.deps.rewardDice.bet),
        dieLabel: this.deps.rewardDice.bet,
        buttonLabel: 'FINISH',
      });
      this.reward.once('continue', () => this.exit());
    });
  }

  /** Emit a locally-authored GameUpdate through the same path a server one
   *  takes (NetworkManager is the EventEmitter GameFlowController listens on). */
  private pushUpdate(gameState: string, previousState: string): void {
    const pid = this.deps.networkManager.getPlayerId() ?? 'tutorial-player';
    const shooterId = 'tutorial-shooter';
    this.updateVersion += 1;
    const update: GameUpdate = {
      version: this.updateVersion,
      gameState,
      roomId: 'tutorial',
      gameType: 'craps',
      players: [
        { playerId: pid, username: 'You', balance: 10_000_000, bet: 0, isReady: true, result: null, isActive: true },
        { playerId: shooterId, username: 'Shooter', balance: 10_000_000, bet: 5000, isReady: true, result: null, isActive: true },
      ],
      bankerId: shooterId,
      currentPlayerId: shooterId,
      pot: 0,
      roundNumber: 1,
      timeRemaining: 0,
      betZones: [],
      bankerHasRolled: true,
      availableSidebets: ['shooter_wins', 'shooter_loses'],
      rollAuthorizedFor: [],
      rollState: null,
      playerRollStates: {},
      hiLoContenders: [],
      hiLoRolls: {},
      hiLoRounds: [],
      customState: {},
      timestamp: Date.now(),
      animated: true,
      previousState,
      lastRollResult: null,
      rollWins: [],
      turnAction: null,
      tieResult: null,
      forfeit: null,
      timer: null,
    };
    this.deps.networkManager.emit('game:update', update);
  }

  // ---- skip / teardown ---------------------------------------------------

  private handleSkip(): void {
    this.deps.networkManager.skipTutorial();
    this.exit();
  }

  private exit(): void {
    if (!this.active) return;
    this.active = false;
    this.coach.hide();
    this.reward.hide();
    this.teardown();
    this.deps.onExit();
  }

  private teardown(): void {
    if (this.winTimer != null) {
      clearTimeout(this.winTimer);
      this.winTimer = null;
    }

    // Module 2 betting teardown (mirrors cleanupGameScene's betting half).
    // No exitBettingMode (private + animation-only): we dispose the whole stack
    // and stop the engine right after, so its wallet/camera tweens are moot.
    if (this.module2Active) {
      const gfc = this.deps.gameFlowController;
      this.unsubBetting?.();
      this.unsubBetting = null;
      this.bettingHUD?.dispose();
      this.bettingHUD = null;
      this.betting?.dispose();
      this.betting = null;
      this.bettingAssetLoader = null;
      gfc.setBettingController(null);
      gfc.setBettingHUD3D(null);
      gfc.setDiceController(null);
      gfc.setCameraController(null);
      // Release the craps scene's anchored lights (about to be disposed below)
      // so the gfc doesn't hold stale references until the next real game.
      gfc.setAnchoredLights([]);
      gfc.setPlayerProfilesOverlay(this.deps.playerProfilesOverlay);
      gfc.resetStateMachine();
      this.module2Active = false;
    }

    this.unsubUpdate?.();
    this.unsubUpdate = null;
    this.detachWallListeners();
    this.dice?.dispose();
    this.dice = null;
    this.camera?.reset();
    this.camera = null;
    this.scene = null;
    const scene = this.engine.scene;
    while (scene.children.length > 0) scene.remove(scene.children[0]);
    this.engine.physics.dispose();
    this.engine.stop();
  }

  destroy(): void {
    this.teardown();
    this.coach.destroy();
    this.reward.destroy();
  }

  // ---- helpers -----------------------------------------------------------

  private thumb(id: string): string {
    return `${this.deps.cdnBase ?? ''}/dice/${id}/thumb-128.png`;
  }

  /** Average the live dice world positions and project to a screen-space box. */
  private diceClusterRect(): ScreenRect | null {
    const dice = this.dice?.getDice() ?? [];
    if (dice.length === 0) return null;
    this._v.set(0, 0, 0);
    for (const die of dice) this._v.add(die.getPosition());
    this._v.multiplyScalar(1 / dice.length);
    this._v.project(this.engine.camera);

    const canvas = this.engine.canvas;
    const rect = canvas.getBoundingClientRect();
    const sx = (this._v.x * 0.5 + 0.5) * rect.width + rect.left;
    const sy = (-this._v.y * 0.5 + 0.5) * rect.height + rect.top;
    const size = 110;
    return { x: sx - size / 2, y: sy - size / 2, width: size, height: size };
  }
}
